<template>
  <div class="flex-1">
    <label :for="inputId" class="block text-sm text-gray-700">
      {{ label }}
      <span v-if="required" class="ml-1 text-red-600">*</span>
    </label>
    <div class="relative">
      <Field v-model="fieldValue" :name="name" type="hidden" />
      <Listbox :model-value="selectedCountryName" @update:model-value="onSelect">
        <ListboxButton
          :id="inputId"
          class="block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-left text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm cursor-pointer"
          @keydown="blockEnterBehavior"
        >
          <span class="block truncate">
            {{ selectedCountryName || placeholder }}
          </span>
          <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
          </span>
        </ListboxButton>

        <transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ListboxOptions
            class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-inset ring-gray-300 focus:outline-hidden sm:text-sm"
          >
            <ListboxOption
              as="template"
              v-for="country in sortedCountries"
              :key="country.code || '__placeholder__'"
              :value="country.nameFr || ''"
              v-slot="{ active, selected }"
            >
              <li
                :class="[
                  selected ? 'text-white bg-primary' : 'text-gray-900',
                  active ? 'bg-gray-100' : '',
                  'relative select-none py-2 pl-3 pr-3 cursor-pointer text-sm',
                ]"
              >
                <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                  {{ country.nameFr || placeholder }}
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </Listbox>
    </div>
    <ErrorMessage :name="name" class="mt-2 text-sm text-red-600" />
  </div>
</template>

<script>
import { Field, ErrorMessage } from "vee-validate";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/solid";
import { countries } from "@estaly/ui";
import { blockEnterBehavior } from "@/utils/blockEnterBehavior";

export default {
  name: "CountryListboxField",
  components: {
    Field,
    ErrorMessage,
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    ChevronDownIcon,
  },
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Sélectionner un pays",
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      countriesList: countries,
      fieldValue: this.modelValue || "",
    };
  },
  watch: {
    modelValue: {
      handler(val) {
        this.fieldValue = val || "";
      },
      immediate: true,
    },
    fieldValue(val) {
      if (val !== this.modelValue) {
        this.$emit("update:modelValue", val || "");
      }
    },
  },
  computed: {
    inputId() {
      return this.name.replace(/\./g, "-");
    },
    selectedCountryName() {
      return this.modelValue || "";
    },
    // Placeholder first, then France, then alphabetical by nameFr
    sortedCountries() {
      const list = [...this.countriesList];
      const fr = list.find((c) => c.code === "FR");
      const rest = list.filter((c) => c.code !== "FR").sort((a, b) => (a.nameFr || "").localeCompare(b.nameFr || ""));
      const sortedList = fr ? [fr, ...rest] : rest;
      return [{ code: "", name: "", nameFr: "" }, ...sortedList];
    },
  },
  methods: {
    onSelect(value) {
      this.fieldValue = value || "";
    },
    blockEnterBehavior,
  },
};
</script>
